import { useCallback, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { usePlayerStore } from '../store/playerStore';
import { useFleetStore } from '../store/fleetStore';
import { useUniverseStore } from '../store/universeStore';
import { useNow } from '../hooks/useNow';
import { useMissionLocks } from '../hooks/useAccountStanding';
import { usePlanetResource } from '../hooks/usePlanetResource';
import PaginatedLogSection from '../components/PaginatedLogSection';
import FlightLogDetailsModal from '../components/FlightLogDetailsModal';
import SendFleetForm from '../components/SendFleetForm';
import ResourceQuantities from '../components/ResourceQuantities';
import { hasResourceQuantities } from '../utils/resourceQuantities';
import { Icon } from '../components/ui/Icon';
import { Badge } from '../components/ui/Badge';
import { Chip } from '../components/ui/Chip';
import { Panel } from '../components/ui/Panel';
import SunAttackAlertsPanel from '../components/SunAttackAlertsPanel';
import { formatClock, formatCoords, formatDateTime, shipSummary } from '../utils/fleetFormat';
import { formatCountdown } from '../utils/formatters';
import { getShipIcon } from '../utils/shipIcons';
import { MISSION_ICON, MISSION_TONE } from '../utils/missionMeta';
import { raceAccentStyle, RACE_BG_TINT } from '../utils/raceColors';
import { FleetProgressTracker } from '../components/FleetProgressTracker';
import { zoneAccentStyle } from '../components/planet/zoneAccent';
import { recallFleet } from '../services/fleet.service';
import { getFlightLogs, type FlightLogCursor } from '../services/flightLog.service';
import { getSpyReports, type SpyReportCursor } from '../services/spyReport.service';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import {
    BUILDING_NAMES,
    FLEET_MISSION_DEFINITIONS,
    PLAYER_PLANET_MISSIONS,
    type Fleet,
    type FleetMissionType,
    type FlightLog,
    type GalaxyPosition,
    type PlayerRace,
    type ScienceId,
    type ShipId,
    type SpyReport,
} from '@kaeliux/shared';

/**
 * Fleets page (issues #16/#17): active-fleet tracking (list, countdowns,
 * recall) plus the dispatch form (ship selection, mission, target, speed,
 * cargo). Every number shown in the form — distance, travel time, gas — is
 * a DISPLAY HELPER computed with the same game-engine formulas the server
 * uses; the server recomputes and re-validates everything on send
 * (docs/game-details/flight_management_system.md §6.1/§25).
 */

// ---------------------------------------------------------------------------
// Active fleet row
// ---------------------------------------------------------------------------

interface FleetRowProps {
    fleet: Fleet;
    race: PlayerRace;
    planets: { id: string; planetType: number }[];
    now: number;
    onRecall: (fleetId: string) => Promise<void>;
}

function FleetRow({ fleet, race, planets, now, onRecall }: FleetRowProps) {
    const originPlanetType = planets.find((p) => p.id === fleet.originPlanetId)?.planetType ?? 1;
    const [isRecalling, setIsRecalling] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const def = FLEET_MISSION_DEFINITIONS[fleet.mission];
    const outbound = fleet.status === 'outbound';
    // Espionage/Sun Support sit at the target for a fixed loiter period
    // before auto-returning — recall works during that window too.
    const loiters = def.loiterSeconds !== undefined;
    const isLoitering = !outbound && loiters && !fleet.recalled;
    const dueMs = (outbound ? fleet.arrivesAt : fleet.returnsAt).getTime() - now;
    const statusLabel = outbound
        ? `Outbound — arrives in ${formatCountdown(dueMs)}`
        : isLoitering
            ? `At target — auto-returns in ${formatCountdown(dueMs)}`
            : `${fleet.recalled ? 'Recalled' : 'Returning'} — lands in ${formatCountdown(dueMs)}`;
    const canRecall = outbound
        ? fleet.arrivesAt.getTime() > now
        : isLoitering && fleet.returnsAt.getTime() > now;
    const hasCargo = hasResourceQuantities(fleet.cargo);

    const departedMs = fleet.departedAt.getTime();
    const arrivesMs = fleet.arrivesAt.getTime();
    const returnsMs = fleet.returnsAt.getTime();
    let legProgress: number;
    if (outbound) {
        const legMs = arrivesMs - departedMs;
        legProgress = legMs > 0 ? (now - departedMs) / legMs : 0;
    } else if (fleet.recalled) {
        const turnaroundMs = fleet.updatedAt.getTime();
        const legMs = returnsMs - turnaroundMs;
        legProgress = legMs > 0 ? (now - turnaroundMs) / legMs : 0;
    } else {
        const legMs = returnsMs - arrivesMs;
        legProgress = legMs > 0 ? (now - arrivesMs) / legMs : 0;
    }
    const flightProgress = Math.min(1, Math.max(0, legProgress));

    async function handleRecall() {
        setIsRecalling(true);
        setError(null);
        try {
            await onRecall(fleet.id);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Recall failed');
        } finally {
            setIsRecalling(false);
        }
    }

    const shipEntries = Object.entries(fleet.ships) as [string, number][];

    const leadShipId = Number(shipEntries.find(([, count]) => count > 0)?.[0] ?? 3) as ShipId;

    return (
        <Card
            className="flex flex-col sm:flex-row sm:items-start gap-3"
            style={{ ...raceAccentStyle(race), backgroundImage: `linear-gradient(${RACE_BG_TINT[race]}, ${RACE_BG_TINT[race]})` }}
            data-testid="fleet-row"
            data-mission={fleet.mission}
        >
            <div className="flex items-center gap-2 shrink-0">
                <Icon name={MISSION_ICON[fleet.mission]} size={20} className="text-[color:var(--zone-accent)]" />
                <div className="flex items-center gap-1 flex-wrap">
                    {shipEntries.filter(([, count]) => count > 0).map(([id]) => (
                        <img
                            key={id}
                            src={getShipIcon(race, Number(id) as ShipId)}
                            alt=""
                            className="w-6 h-6 rounded-control border border-hairline bg-surface-void/60 object-contain"
                        />
                    ))}
                </div>
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <Badge tone={MISSION_TONE[fleet.mission]}>{def.name}</Badge>
                    <span className="text-content-muted text-label">
                        {formatCoords(fleet.origin)} → {formatCoords(fleet.target)}
                    </span>
                </div>
                <p className="text-content-secondary text-body mt-1 truncate" title={shipSummary(fleet.ships, race)}>
                    {shipSummary(fleet.ships, race)}
                </p>
                {hasCargo && <p className="text-content-muted text-label mt-0.5"><ResourceQuantities amounts={fleet.cargo} /></p>}
                {fleet.arrivalReport && (
                    <p className="text-content-muted text-label mt-0.5 italic">{fleet.arrivalReport.message}</p>
                )}
                <p
                    className={`text-value mt-1.5 ${outbound ? 'text-content-secondary' : isLoitering ? 'text-status-info' : 'text-status-warning'}`}
                    data-testid="fleet-status"
                >
                    {statusLabel}
                </p>
                <FleetProgressTracker
                    progress={flightProgress}
                    outbound={outbound}
                    race={race}
                    originPlanetType={originPlanetType}
                    mission={fleet.mission}
                    target={fleet.target}
                    leadShipId={leadShipId}
                />
                <p className="text-content-muted text-label mt-0.5" data-testid="fleet-eta">
                    Arrives {formatClock(fleet.arrivesAt)} · Returns {formatClock(fleet.returnsAt)}
                </p>
                {error && <p className="text-status-danger text-label mt-1">{error}</p>}
            </div>
            {canRecall && (
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleRecall}
                    disabled={isRecalling}
                    loading={isRecalling}
                    className="shrink-0 self-center"
                >
                    Recall
                </Button>
            )}
        </Card>
    );
}

// ---------------------------------------------------------------------------
// Page content
// ---------------------------------------------------------------------------

function FleetsPageContent({ race }: { race: PlayerRace }) {
    const { state: { planet, isLoading: isPlanetLoading } } = usePlanetResource();
    const allPlanets = usePlanetStore((s) => s.planets);
    const { sciences, shipUpgrades, premium } = usePlayerStore();
    const darkMatter = premium?.darkMatter ?? 0;
    const { flightMultiplier } = useUniverseStore();
    const now = useNow();

    // Fleets live in a store rather than page state so the game loop can keep
    // resolving arrivals and returns while the player is on another page.
    const view = useFleetStore((s) => s.view);
    const loadError = useFleetStore((s) => s.error);
    const logsRefreshKey = useFleetStore((s) => s.refreshSeq);
    const loadFleets = useFleetStore((s) => s.loadFleets);
    const [showEspionage, setShowEspionage] = useState(true);

    // The galaxy map deep-links here with a preselected mission/target
    // (`/fleets?mission=attack&target=1:2:5`) — used as INITIAL form state only.
    const [searchParams] = useSearchParams();
    const initialForm = useRef((() => {
        const missionParam = searchParams.get('mission') as FleetMissionType | null;
        const mission = missionParam && PLAYER_PLANET_MISSIONS.includes(missionParam) ? missionParam : undefined;
        const [galaxy, system, position] = (searchParams.get('target') ?? '').split(':').map(Number);
        const target: GalaxyPosition =
            [galaxy, system, position].every((n) => n !== undefined && Number.isInteger(n) && n >= 0)
                ? { galaxy: galaxy!, system: system!, position: position! }
                : { galaxy: 1, system: 1, position: 1 };
        return { mission, target };
    })());

    const missionLocks = useMissionLocks(PLAYER_PLANET_MISSIONS);

    const navigate = useNavigate();
    const [modalLog, setModalLog] = useState<FlightLog | null>(null);
    const [modalSpyReport, setModalSpyReport] = useState<SpyReport | null>(null);

    const { profile } = useAuthStore();

    const handleRecall = useCallback(async (fleetId: string) => {
        await recallFleet(fleetId);
        await loadFleets();
    }, [loadFleets]);

    if (isPlanetLoading || !planet || sciences === null) {
        return (
            <div className="min-h-screen bg-transparent flex items-center justify-center">
                <Skeleton variant="text" width={180} height={28} />
            </div>
        );
    }

    const espionageFleets = view?.fleets.filter((f) => f.mission === 'espionage') ?? [];
    const normalFleets = view?.fleets.filter((f) => f.mission !== 'espionage') ?? [];
    const visibleFleets = showEspionage ? view?.fleets ?? [] : normalFleets;

    return (
        <div className="bg-transparent flex flex-col" style={zoneAccentStyle('military')}>
            <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <h1 className="text-heading font-bold text-content-primary">Fleets</h1>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2" data-testid="fleet-counts">
                            <Chip tone="accent" leadingIcon={<Icon name="fleets" size={14} />}>
                                Flights: {view?.activeCount ?? 0} / {view?.maxActive ?? 1}
                            </Chip>
                            <Chip tone="warning" leadingIcon={<Icon name="science" size={14} />}>
                                Espionage: {view?.espionageCount ?? 0} / {view?.maxEspionage ?? 5}
                            </Chip>
                        </div>
                        <Link to="/fleets/simulator" className={linkButtonClasses('secondary', 'sm')} data-testid="battle-simulator-link">Battle Simulator</Link>
                    </div>
                </div>

                <SunAttackAlertsPanel />

                {/* ---------------- Active fleets ---------------- */}
                <Panel variant="solid" className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <h2 className="text-title text-content-primary font-semibold">Active Fleets</h2>
                        {espionageFleets.length > 0 && (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowEspionage((v) => !v)}
                            >
                                {showEspionage ? 'Hide espionage' : `Show espionage (${espionageFleets.length})`}
                            </Button>
                        )}
                    </div>
                    {loadError && <p className="text-status-danger text-body">{loadError}</p>}
                    {visibleFleets.length === 0 ? (
                        <p className="text-content-muted text-body">No fleets in flight.</p>
                    ) : (
                        visibleFleets.map((fleet) => (
                            <FleetRow key={fleet.id} fleet={fleet} race={race} planets={allPlanets} now={now} onRecall={handleRecall} />
                        ))
                    )}
                </Panel>

                {/* ---------------- Send fleet ---------------- */}
                <SendFleetForm
                    planet={planet}
                    race={race}
                    sciences={sciences as Partial<Record<ScienceId, number>>}
                    shipUpgrades={shipUpgrades ?? {}}
                    darkMatter={darkMatter}
                    flightMultiplier={flightMultiplier}
                    allowedMissions={PLAYER_PLANET_MISSIONS}
                    missionLocks={missionLocks}
                    initialMission={initialForm.current.mission}
                    initialTarget={initialForm.current.target}
                    emptyShipsMessage={
                        <>
                            No ships docked at this planet — build some at the{' '}
                            <Link to="/shipyard" className="text-content-secondary underline">{BUILDING_NAMES.shipyard}</Link>.
                        </>
                    }
                    onSent={(result) => usePlanetStore.setState({ currentPlanet: result.planet })}
                />

                {/* ---------------- Flight logs / Battle reports / Espionage reports ---------------- */}
                {profile?.id && (
                    <>
                        <PaginatedLogSection<FlightLog, FlightLogCursor>
                            title="Flight Logs"
                            emptyMessage="No flight logs yet — they appear here whenever a fleet arrives or returns."
                            refreshKey={logsRefreshKey}
                            keyOf={(log) => log.id}
                            fetchPage={(cursor) =>
                                getFlightLogs(profile.id, 'general', cursor).then((p) => ({
                                    items: p.logs,
                                    nextCursor: p.nextCursor,
                                }))
                            }
                            renderCard={(log) => (
                                <button
                                    type="button"
                                    onClick={() => setModalLog(log)}
                                    className="w-full text-left rounded-tile border border-hairline bg-surface-chip p-2 hover:border-[color:var(--zone-accent)] transition-colors"
                                    data-testid="flight-log-card"
                                >
                                    <p className="text-content-primary text-label font-semibold flex items-center gap-1 min-w-0">
                                        <Icon name={MISSION_ICON[log.mission]} size={12} className="text-[color:var(--zone-accent)] shrink-0" />
                                        <span className="truncate">{FLEET_MISSION_DEFINITIONS[log.mission].name} · {log.event === 'arrival' ? 'Arrived' : 'Returned'}</span>
                                    </p>
                                    <p className="text-content-muted text-micro">{formatDateTime(log.occurredAt)}</p>
                                    <p className="text-content-muted text-micro truncate italic">{log.message}</p>
                                </button>
                            )}
                        />

                        <PaginatedLogSection<FlightLog, FlightLogCursor>
                            title="Battle Reports"
                            emptyMessage="No battle reports yet."
                            refreshKey={logsRefreshKey}
                            keyOf={(log) => log.id}
                            fetchPage={(cursor) =>
                                getFlightLogs(profile.id, 'battleReport', cursor).then((p) => ({
                                    items: p.logs,
                                    nextCursor: p.nextCursor,
                                }))
                            }
                            renderCard={(log) => (
                                <button
                                    type="button"
                                    onClick={() => navigate(`/fleets/battle-report/${log.id}`)}
                                    className="w-full text-left rounded-tile border border-hairline bg-surface-chip p-2 hover:border-[color:var(--zone-accent)] transition-colors"
                                    data-testid="battle-report-card"
                                >
                                    <p className="text-content-primary text-label font-semibold flex items-center gap-1 min-w-0">
                                        <Icon name={MISSION_ICON[log.mission]} size={12} className="text-[color:var(--zone-accent)] shrink-0" />
                                        <span className="truncate">{FLEET_MISSION_DEFINITIONS[log.mission].name} · {log.event === 'arrival' ? 'Arrived' : 'Returned'}</span>
                                        {log.saved && (
                                            <Icon
                                                name="star"
                                                size={11}
                                                fill="currentColor"
                                                className="shrink-0"
                                                aria-hidden="true"
                                                data-testid="saved-star"
                                            />
                                        )}
                                    </p>
                                    <p className="text-content-muted text-micro">{formatDateTime(log.occurredAt)}</p>
                                    <p className="text-content-muted text-micro truncate italic">{log.message}</p>
                                </button>
                            )}
                        />

                        <PaginatedLogSection<SpyReport, SpyReportCursor>
                            title="Espionage Reports"
                            emptyMessage="No espionage reports yet."
                            refreshKey={logsRefreshKey}
                            keyOf={(report) => report.id}
                            fetchPage={(cursor) =>
                                getSpyReports(profile.id, cursor).then((p) => ({
                                    items: p.reports,
                                    nextCursor: p.nextCursor,
                                }))
                            }
                            renderCard={(report) => (
                                <button
                                    type="button"
                                    onClick={() => setModalSpyReport(report)}
                                    className="w-full text-left rounded-tile border border-hairline bg-surface-chip p-2 hover:border-[color:var(--zone-accent)] transition-colors"
                                    data-testid="spy-report-card"
                                >
                                    <p className="text-content-primary text-label font-semibold flex items-center gap-1 min-w-0">
                                        <Icon name="science" size={12} className="text-[color:var(--zone-accent)] shrink-0" />
                                        <span className="truncate">{report.targetPlanetName ?? formatCoords(report.target)}</span>
                                    </p>
                                    <p className="text-content-muted text-micro">{formatDateTime(report.capturedAt)}</p>
                                </button>
                            )}
                        />
                    </>
                )}
            </main>

            <FlightLogDetailsModal
                open={modalLog !== null || modalSpyReport !== null}
                onClose={() => { setModalLog(null); setModalSpyReport(null); }}
                race={race}
                log={modalLog}
                spyReport={modalSpyReport}
            />
        </div>
    );
}

export default function FleetsPage() {
    const { profile } = useAuthStore();
    const activePlanetId = usePlanetStore((s) => s.activePlanetId);

    const planetId = activePlanetId ?? profile?.mainPlanetId ?? '';

    if (!profile || !planetId || !profile.race) {
        return (
            <div className="min-h-screen bg-transparent flex items-center justify-center">
                <Skeleton variant="text" width={160} height={28} />
            </div>
        );
    }

    return (
        <FleetsPageContent race={profile.race} />
    );
}
